/**
 * İstemci tarafı etkileşimler.
 * Veri: ../data/words  ·  Oyun mantığı: ../game/engine  ·  Kayıt: ./storage
 */
import { WORDS, TOTAL_WORDS, getWord, getGroup, type Word } from '../data/words'
import {
  PracticeSession,
  MixedRound,
  MatchingGame,
  type ChoiceQuestion,
  type AnswerResult
} from '../game/engine'
import { createProgressStore } from './storage'

/* ------------------------------------------------------------------ */
/* Yardımcılar                                                         */
/* ------------------------------------------------------------------ */
const $ = <T extends HTMLElement = HTMLElement>(sel: string, root: ParentNode = document) =>
  root.querySelector<T>(sel)
const $$ = <T extends HTMLElement = HTMLElement>(sel: string, root: ParentNode = document) =>
  Array.from(root.querySelectorAll<T>(sel))

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
const scrollBehavior = (): ScrollBehavior => (reduceMotion() ? 'auto' : 'smooth')

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  text?: string
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v)
  if (text !== undefined) node.textContent = text
  return node
}

const announcer = $('#announcer')
function announce(msg: string) {
  if (!announcer) return
  announcer.textContent = ''
  window.setTimeout(() => (announcer.textContent = msg), 30)
}

function wordOrThrow(id: string): Word {
  const w = getWord(id)
  if (!w) throw new Error(`Kelime bulunamadı: ${id}`)
  return w
}

/* ------------------------------------------------------------------ */
/* Kelime kartları ve ilerleme                                         */
/* ------------------------------------------------------------------ */
const progress = createProgressStore()
const cards = $$<HTMLLIElement>('.word-card')

function setCardOpen(card: HTMLElement, open: boolean) {
  card.classList.toggle('is-open', open)
  const toggle = $<HTMLButtonElement>('[data-toggle]', card)
  const text = $('[data-toggle-text]', card)
  const reveal = $('[data-reveal]', card)
  toggle?.setAttribute('aria-expanded', String(open))
  if (text) text.textContent = open ? 'Anlamı Gizle' : 'Anlamı Göster'
  if (reveal) {
    if (open) reveal.removeAttribute('inert')
    else reveal.setAttribute('inert', '')
  }
}

function renderLearned(card: HTMLElement) {
  const id = card.dataset.wordId as string
  const learned = progress.has(id)
  card.classList.toggle('is-learned', learned)
  const btn = $<HTMLButtonElement>('[data-learn]', card)
  const txt = $('[data-learn-text]', card)
  const label = $('[data-learned-label]', card)
  btn?.setAttribute('aria-pressed', String(learned))
  if (txt) txt.textContent = learned ? 'Öğrenildi — geri al' : 'Öğrendim'
  if (label) label.hidden = !learned
}

function renderProgress() {
  const n = progress.count()
  const text = $('#progress-text')
  const fill = $('#progress-fill')
  if (text) {
    text.textContent =
      n === TOTAL_WORDS
        ? `${TOTAL_WORDS} kelimenin tamamını öğrendin.`
        : `${TOTAL_WORDS} kelimeden ${n} tanesini öğrendin.`
  }
  if (fill) fill.style.width = `${(n / TOTAL_WORDS) * 100}%`
  const resetBtn = $<HTMLButtonElement>('#progress-reset')
  if (resetBtn) resetBtn.disabled = n === 0
  $('#progress-panel')?.classList.toggle('is-complete', n === TOTAL_WORDS)
}

function updateRevealAllLabel() {
  const visible = cards.filter((c) => !c.hidden)
  const allOpen = visible.length > 0 && visible.every((c) => c.classList.contains('is-open'))
  const btn = $('#reveal-all')
  if (btn) btn.textContent = allOpen ? 'Tüm Anlamları Gizle' : 'Tüm Anlamları Göster'
}

cards.forEach((card) => {
  setCardOpen(card, false)
  renderLearned(card)
  $('[data-toggle]', card)?.addEventListener('click', () => {
    setCardOpen(card, !card.classList.contains('is-open'))
    updateRevealAllLabel()
  })
  $('[data-learn]', card)?.addEventListener('click', () => {
    const id = card.dataset.wordId as string
    progress.set(id, !progress.has(id))
    renderLearned(card)
    renderProgress()
  })
})
renderProgress()

$('#reveal-all')?.addEventListener('click', () => {
  const visible = cards.filter((c) => !c.hidden)
  const allOpen = visible.every((c) => c.classList.contains('is-open'))
  visible.forEach((c) => setCardOpen(c, !allOpen))
  updateRevealAllLabel()
})

$('#progress-reset')?.addEventListener('click', () => {
  if (progress.count() === 0) return
  if (!window.confirm('Öğrendim olarak işaretlediğin tüm kelimeler sıfırlansın mı?')) return
  progress.clear()
  cards.forEach(renderLearned)
  renderProgress()
  announce('Öğrenme ilerlemen sıfırlandı.')
})

if (!progress.persistent) {
  const note = $('.progress-panel__note')
  if (note) note.textContent = 'Bu tarayıcı yerel kayda izin vermiyor; işaretlerin sayfa kapanınca silinir.'
}

/* Grup süzgeci */
const filterButtons = $$<HTMLButtonElement>('.filter-btn')
function applyFilter(filter: string) {
  filterButtons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.filter === filter)))
  let shown = 0
  cards.forEach((c) => {
    const visible = filter === 'all' || c.dataset.group === filter
    c.hidden = !visible
    if (visible) shown++
  })
  const status = $('#filter-status')
  if (status) {
    status.textContent =
      filter === 'all'
        ? `${shown} kelimenin tümü gösteriliyor.`
        : `${getGroup(Number(filter) as 1).name} grubundaki ${shown} kelime gösteriliyor.`
  }
  updateRevealAllLabel()
}
filterButtons.forEach((b) => b.addEventListener('click', () => applyFilter(b.dataset.filter || 'all')))

function focusCard(card: HTMLElement) {
  card.scrollIntoView({ behavior: scrollBehavior(), block: 'center' })
  card.focus({ preventScroll: true })
  card.classList.remove('is-flash')
  void card.offsetWidth
  card.classList.add('is-flash')
}

function goToWord(id: string) {
  const card = $<HTMLElement>(`#kelime-${id}`)
  if (!card) return
  if (card.hidden) applyFilter('all')
  setCardOpen(card, true)
  updateRevealAllLabel()
  focusCard(card)
}

function goToGroup(groupId: string) {
  applyFilter(groupId)
  const groupCards = cards.filter((c) => c.dataset.group === groupId)
  groupCards.forEach((c) => setCardOpen(c, true))
  updateRevealAllLabel()
  const first = groupCards[0]
  if (first) focusCard(first)
  announce(`${getGroup(Number(groupId) as 1).name} grubunun ${groupCards.length} kartı açıldı.`)
}

$$<HTMLButtonElement>('[data-goto-group]').forEach((b) =>
  b.addEventListener('click', () => goToGroup(b.dataset.gotoGroup as string))
)

// #kelime-xxx bağlantıları: kartı aç ve odakla
document.addEventListener('click', (e) => {
  const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href^="#kelime-"]')
  if (!a) return
  const id = a.getAttribute('href')!.slice('#kelime-'.length)
  if (id === 'listesi' || id === 'hazinesi' || !getWord(id)) return
  e.preventDefault()
  history.replaceState(null, '', `#kelime-${id}`)
  goToWord(id)
})

function handleInitialHash() {
  const h = location.hash
  if (h.startsWith('#kelime-')) {
    const id = h.slice('#kelime-'.length)
    if (getWord(id)) goToWord(id)
  }
}

/* ------------------------------------------------------------------ */
/* Oyun: ortak seçenek çizimi                                          */
/* ------------------------------------------------------------------ */
function renderOptions(container: HTMLElement, q: ChoiceQuestion, onPick: (id: string) => void) {
  container.replaceChildren()
  container.classList.toggle('options--long', q.kind === 'word-to-meaning')
  const letters = ['A', 'B', 'C', 'D']
  q.options.forEach((opt, i) => {
    const btn = el('button', { type: 'button', class: 'option', 'data-option': opt.id })
    if (q.kind === 'meaning-to-word') btn.setAttribute('lang', 'tr')
    const key = el('span', { class: 'option__key', 'aria-hidden': 'true' }, letters[i])
    const label = el('span', { class: 'option__label' }, opt.label)
    const tag = el('span', { class: 'option__tag' })
    btn.append(key, label, tag)
    btn.addEventListener('click', () => onPick(opt.id))
    container.append(btn)
  })
}

function markOptions(container: HTMLElement, res: AnswerResult) {
  $$<HTMLButtonElement>('.option', container).forEach((btn) => {
    const id = btn.dataset.option
    const tag = $('.option__tag', btn)!
    btn.disabled = true
    if (id === res.correctId) {
      btn.dataset.state = 'correct'
      tag.textContent = res.correct ? '✓ Doğru' : '✓ Doğru cevap'
    } else if (id === res.selectedId) {
      btn.dataset.state = 'wrong'
      tag.textContent = '✗ Senin seçimin'
    } else {
      btn.dataset.state = 'dim'
    }
  })
}

function feedbackFor(res: AnswerResult): { html: DocumentFragment; plain: string } {
  const right = wordOrThrow(res.correctId)
  const frag = document.createDocumentFragment()
  const title = el('p', { class: 'feedback__title' })
  const body = el('p', { class: 'feedback__body' })
  if (res.correct) {
    title.textContent = 'Doğru!'
    body.append(el('strong', { lang: 'tr' }, right.word), document.createTextNode(` — ${right.meaning}.`))
  } else {
    const picked = wordOrThrow(res.selectedId)
    title.textContent = 'Bu sefer olmadı.'
    body.append(
      document.createTextNode('Doğru cevap '),
      el('strong', { lang: 'tr' }, right.word),
      document.createTextNode(` — ${right.meaning}. Seçtiğin `),
      el('strong', { lang: 'tr' }, picked.word),
      document.createTextNode(` ise “${picked.meaning}” anlamına gelir.`)
    )
  }
  frag.append(title, body)
  const plain = res.correct
    ? `Doğru! ${right.word}: ${right.meaning}.`
    : `Yanlış. Doğru cevap ${right.word}: ${right.meaning}.`
  return { html: frag, plain }
}

function showFeedback(box: HTMLElement, res: AnswerResult) {
  const { html } = feedbackFor(res)
  box.replaceChildren(html)
  box.dataset.state = res.correct ? 'correct' : 'wrong'
}

function clearFeedback(box: HTMLElement) {
  box.replaceChildren()
  delete box.dataset.state
}

/* ------------------------------------------------------------------ */
/* Mod 1 — Anlamdan Kelimeye                                           */
/* ------------------------------------------------------------------ */
const practice = new PracticeSession()
const pPrompt = $('#practice-prompt')!
const pOptions = $('#practice-options')!
const pFeedback = $('#practice-feedback')!
const pNext = $<HTMLButtonElement>('#practice-next')!
const pScore = $('#practice-score')!

function renderPracticeScore() {
  pScore.textContent = `Bu oturumda ${practice.correctCount} / ${practice.answeredCount} doğru`
}

function renderPractice() {
  const q = practice.current
  pPrompt.textContent = `“${q.prompt}”`
  renderOptions(pOptions, q, (id) => {
    const res = practice.answer(id)
    if (!res.counted) return
    markOptions(pOptions, res)
    showFeedback(pFeedback, res)
    renderPracticeScore()
    pNext.disabled = false
    pNext.focus({ preventScroll: true })
  })
  clearFeedback(pFeedback)
  pNext.disabled = true
  renderPracticeScore()
}

pNext.addEventListener('click', () => {
  practice.next()
  renderPractice()
  $<HTMLButtonElement>('.option', pOptions)?.focus({ preventScroll: true })
})
$('#practice-reset')?.addEventListener('click', () => {
  practice.reset()
  renderPractice()
  announce('Sayaç sıfırlandı. Yeni soru hazır.')
})
renderPractice()

/* ------------------------------------------------------------------ */
/* Mod 2 — Eşleştir                                                    */
/* ------------------------------------------------------------------ */
const match = new MatchingGame()
const mWords = $('#match-words')!
const mMeanings = $('#match-meanings')!
const mFeedback = $('#match-feedback')!
const mNext = $<HTMLButtonElement>('#match-next')!
const mScore = $('#match-score')!
const mResult = $('#match-result')!
const mBoard = $('#match-board')!
const MEANING_KEYS = ['A', 'B', 'C', 'D']

let selectedWord: string | null = null
let selectedMeaning: string | null = null

function meaningKey(id: string): string {
  return MEANING_KEYS[match.currentMeanings.findIndex((w) => w.id === id)] ?? ''
}

function renderMatchScore() {
  mScore.textContent = `Set ${match.setIndex + 1} / ${match.sets.length} · ${match.totalMatched} / ${TOTAL_WORDS} eşleşme`
}

function renderMatch() {
  selectedWord = null
  selectedMeaning = null
  mWords.replaceChildren()
  mMeanings.replaceChildren()

  match.currentSet.forEach((w) => {
    const li = el('li')
    const btn = el('button', {
      type: 'button',
      class: 'match-item match-item--word',
      'data-match-word': w.id,
      'aria-pressed': 'false',
      draggable: 'true',
      lang: 'tr'
    })
    btn.append(el('span', { class: 'match-item__text' }, w.word), el('span', { class: 'match-item__tag' }))
    btn.addEventListener('click', () => pickWord(w.id))
    btn.addEventListener('dragstart', (e) => {
      if (match.matched.has(w.id)) return e.preventDefault()
      e.dataTransfer?.setData('text/plain', w.id)
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
      btn.classList.add('is-dragging')
    })
    btn.addEventListener('dragend', () => btn.classList.remove('is-dragging'))
    li.append(btn)
    mWords.append(li)
  })

  match.currentMeanings.forEach((w, i) => {
    const li = el('li')
    const btn = el('button', {
      type: 'button',
      class: 'match-item match-item--meaning',
      'data-match-meaning': w.id,
      'aria-pressed': 'false'
    })
    btn.append(
      el('span', { class: 'match-item__key', 'aria-hidden': 'true' }, MEANING_KEYS[i]),
      el('span', { class: 'match-item__text' }, w.meaning),
      el('span', { class: 'match-item__tag' })
    )
    btn.addEventListener('click', () => pickMeaning(w.id))
    btn.addEventListener('dragover', (e) => {
      if (match.matched.has(w.id)) return
      e.preventDefault()
      btn.classList.add('is-dropzone')
    })
    btn.addEventListener('dragleave', () => btn.classList.remove('is-dropzone'))
    btn.addEventListener('drop', (e) => {
      e.preventDefault()
      btn.classList.remove('is-dropzone')
      const wordId = e.dataTransfer?.getData('text/plain')
      if (wordId && getWord(wordId)) attemptMatch(wordId, w.id)
    })
    li.append(btn)
    mMeanings.append(li)
  })

  clearFeedback(mFeedback)
  mNext.hidden = true
  mBoard.hidden = false
  mResult.hidden = true
  renderMatchScore()
}

function refreshSelection() {
  $$<HTMLButtonElement>('[data-match-word]', mWords).forEach((b) =>
    b.setAttribute('aria-pressed', String(b.dataset.matchWord === selectedWord))
  )
  $$<HTMLButtonElement>('[data-match-meaning]', mMeanings).forEach((b) =>
    b.setAttribute('aria-pressed', String(b.dataset.matchMeaning === selectedMeaning))
  )
}

function pickWord(id: string) {
  if (match.matched.has(id)) return
  selectedWord = selectedWord === id ? null : id
  if (selectedWord && selectedMeaning) return attemptMatch(selectedWord, selectedMeaning)
  refreshSelection()
}

function pickMeaning(id: string) {
  if (match.matched.has(id)) return
  selectedMeaning = selectedMeaning === id ? null : id
  if (selectedWord && selectedMeaning) return attemptMatch(selectedWord, selectedMeaning)
  refreshSelection()
}

function attemptMatch(wordId: string, meaningId: string) {
  const res = match.tryMatch(wordId, meaningId)
  const wBtn = $<HTMLButtonElement>(`[data-match-word="${wordId}"]`, mWords)!
  const mBtn = $<HTMLButtonElement>(`[data-match-meaning="${meaningId}"]`, mMeanings)!
  const w = wordOrThrow(wordId)
  const m = wordOrThrow(meaningId)
  selectedWord = null
  selectedMeaning = null

  if (res.ok) {
    const key = meaningKey(meaningId)
    ;[wBtn, mBtn].forEach((b) => {
      b.dataset.state = 'matched'
      b.disabled = true
      b.draggable = false
    })
    $('.match-item__tag', wBtn)!.textContent = `✓ ${key}`
    $('.match-item__tag', mBtn)!.textContent = '✓ Eşleşti'
    mFeedback.dataset.state = 'correct'
    mFeedback.replaceChildren(
      el('p', { class: 'feedback__title' }, 'Doğru eşleşme!'),
      (() => {
        const p = el('p', { class: 'feedback__body' })
        p.append(el('strong', { lang: 'tr' }, w.word), document.createTextNode(` — ${w.meaning}.`))
        return p
      })()
    )
  } else {
    ;[wBtn, mBtn].forEach((b) => {
      b.dataset.state = 'wrong'
      window.setTimeout(() => {
        if (b.dataset.state === 'wrong') delete b.dataset.state
      }, 900)
    })
    mFeedback.dataset.state = 'wrong'
    mFeedback.replaceChildren(
      el('p', { class: 'feedback__title' }, 'Bu ikisi eşleşmiyor.'),
      (() => {
        const p = el('p', { class: 'feedback__body' })
        p.append(
          el('strong', { lang: 'tr' }, w.word),
          document.createTextNode(` kelimesi “${m.meaning}” anlamına gelmiyor. Başka bir anlam dene.`)
        )
        return p
      })()
    )
  }
  refreshSelection()
  renderMatchScore()

  if (match.isSetComplete) {
    if (match.isFinished) {
      showMatchResult()
    } else {
      mNext.hidden = false
      mNext.focus({ preventScroll: true })
      announce('Bu seti tamamladın. Sonraki sete geçebilirsin.')
    }
  } else {
    // Odak kaybolmasın: sıradaki eşleşmemiş kelimeye geç
    const nextBtn = $$<HTMLButtonElement>('[data-match-word]', mWords).find((b) => !b.disabled)
    if (res.ok && nextBtn) nextBtn.focus({ preventScroll: true })
  }
}

function showMatchResult() {
  mBoard.hidden = true
  mNext.hidden = true
  clearFeedback(mFeedback)
  mResult.replaceChildren()
  mResult.append(
    el('p', { class: 'result__eyebrow' }, 'Eşleştirme tamamlandı'),
    el('p', { class: 'result__score' }, `${match.totalMatched} / ${TOTAL_WORDS}`),
    el(
      'p',
      { class: 'result__text' },
      match.wrongAttempts === 0
        ? 'On iki kelimenin tamamını hiç hatasız eşleştirdin.'
        : `On iki kelimenin tamamını eşleştirdin. Hatalı deneme sayısı: ${match.wrongAttempts}.`
    )
  )
  const again = el('button', { type: 'button', class: 'btn btn--primary' }, 'Tekrar Oyna')
  again.addEventListener('click', () => {
    match.reset()
    renderMatch()
    $<HTMLButtonElement>('[data-match-word]', mWords)?.focus()
  })
  const actions = el('div', { class: 'result__actions' })
  actions.append(again)
  mResult.append(actions)
  mResult.hidden = false
  mResult.focus({ preventScroll: true })
}

mNext.addEventListener('click', () => {
  if (match.nextSet()) {
    renderMatch()
    $<HTMLButtonElement>('[data-match-word]', mWords)?.focus({ preventScroll: true })
  }
})
$('#match-reset')?.addEventListener('click', () => {
  match.reset()
  renderMatch()
  announce('Eşleştirme baştan başladı.')
})
renderMatch()

/* ------------------------------------------------------------------ */
/* Mod 3 — Karma Tur                                                   */
/* ------------------------------------------------------------------ */
const mixed = new MixedRound()
const xQuestion = $('#mixed-question')!
const xResult = $('#mixed-result')!
const xPrompt = $('#mixed-prompt')!
const xKicker = $('#mixed-kicker')!
const xOptions = $('#mixed-options')!
const xFeedback = $('#mixed-feedback')!
const xNext = $<HTMLButtonElement>('#mixed-next')!
const xCount = $('#mixed-count')!
const xScore = $('#mixed-score')!
const xBar = $('#mixed-bar')!

function renderMixed() {
  const q = mixed.current
  xQuestion.hidden = false
  xResult.hidden = true
  xCount.textContent = `Soru ${mixed.index + 1} / ${mixed.total}`
  xBar.style.width = `${(mixed.index / mixed.total) * 100}%`
  xScore.textContent = `Puan ${mixed.score}`
  xKicker.textContent =
    q.kind === 'meaning-to-word' ? 'Bu anlama gelen kelime hangisi?' : 'Bu kelimenin anlamı hangisi?'
  xPrompt.textContent = q.kind === 'meaning-to-word' ? `“${q.prompt}”` : q.prompt
  if (q.kind === 'word-to-meaning') xPrompt.setAttribute('lang', 'tr')
  else xPrompt.removeAttribute('lang')
  xPrompt.classList.toggle('question__prompt--word', q.kind === 'word-to-meaning')
  renderOptions(xOptions, q, (id) => {
    const res = mixed.answer(id)
    if (!res.counted) return
    markOptions(xOptions, res)
    showFeedback(xFeedback, res)
    xScore.textContent = `Puan ${mixed.score}`
    xBar.style.width = `${((mixed.index + 1) / mixed.total) * 100}%`
    xNext.disabled = false
    xNext.textContent = mixed.isLast ? 'Sonucu Gör' : 'Sonraki Soru'
    xNext.focus({ preventScroll: true })
  })
  clearFeedback(xFeedback)
  xNext.disabled = true
  xNext.textContent = mixed.isLast ? 'Sonucu Gör' : 'Sonraki Soru'
}

function showMixedResult() {
  const r = mixed.result()
  xQuestion.hidden = true
  xResult.replaceChildren()

  let comment: string
  if (r.percent === 100) comment = 'Kusursuz! On iki kelimenin hepsini doğru bildin.'
  else if (r.percent >= 75) comment = 'Çok iyi. Kalan birkaç kelimeyi aşağıdan yeniden inceleyebilirsin.'
  else if (r.percent >= 50) comment = 'İyi bir başlangıç. Yanlış yaptığın kelimelere bir kez daha göz at.'
  else comment = 'Kelime Hazinesi kartlarına göz atıp yeniden dene; ikinci tur daha kolay olacak.'

  const stats = el('dl', { class: 'result__stats' })
  ;[
    ['Doğru', String(r.correct)],
    ['Toplam soru', String(r.total)],
    ['Başarı', `%${r.percent}`]
  ].forEach(([k, v]) => {
    const d = el('div')
    d.append(el('dt', {}, k), el('dd', {}, v))
    stats.append(d)
  })

  xResult.append(
    el('p', { class: 'result__eyebrow' }, 'Karma Tur sonucu'),
    el('p', { class: 'result__score' }, `${r.correct} / ${r.total}`),
    stats,
    el('p', { class: 'result__text' }, comment)
  )

  if (r.mistakes.length) {
    const box = el('details', { class: 'result__review' })
    box.open = true
    box.append(el('summary', {}, `Yeniden incele (${r.mistakes.length} kelime)`))
    const list = el('ul', { class: 'review-list' })
    r.mistakes.forEach((w) => {
      const li = el('li')
      const a = el('a', { href: `#kelime-${w.id}`, lang: 'tr' }, w.word)
      li.append(a, el('span', {}, w.meaning))
      list.append(li)
    })
    box.append(list)
    xResult.append(box)
  }

  const again = el('button', { type: 'button', class: 'btn btn--primary' }, 'Tekrar Oyna')
  again.addEventListener('click', () => {
    mixed.reset()
    renderMixed()
    $<HTMLButtonElement>('.option', xOptions)?.focus({ preventScroll: true })
    announce('Yeni Karma Tur başladı. Puan sıfırlandı.')
  })
  const actions = el('div', { class: 'result__actions' })
  actions.append(again)
  xResult.append(actions)
  xResult.hidden = false
  xResult.focus({ preventScroll: true })
  xResult.scrollIntoView({ behavior: scrollBehavior(), block: 'nearest' })
}

xNext.addEventListener('click', () => {
  if (!mixed.currentResult) return
  if (mixed.isLast) return showMixedResult()
  mixed.next()
  renderMixed()
  $<HTMLButtonElement>('.option', xOptions)?.focus({ preventScroll: true })
})
renderMixed()

/* ------------------------------------------------------------------ */
/* Sekmeler                                                            */
/* ------------------------------------------------------------------ */
const tabs = $$<HTMLButtonElement>('[role="tab"]')
function selectTab(tab: HTMLButtonElement, focus = true) {
  tabs.forEach((t) => {
    const on = t === tab
    t.setAttribute('aria-selected', String(on))
    t.tabIndex = on ? 0 : -1
    const panel = document.getElementById(t.getAttribute('aria-controls')!)
    if (panel) panel.hidden = !on
  })
  if (focus) tab.focus()
}
tabs.forEach((tab, i) => {
  tab.addEventListener('click', () => selectTab(tab))
  tab.addEventListener('keydown', (e) => {
    let next = -1
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % tabs.length
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + tabs.length) % tabs.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = tabs.length - 1
    if (next >= 0) {
      e.preventDefault()
      selectTab(tabs[next])
    }
  })
})

/* ------------------------------------------------------------------ */
/* Söz Haritası (gizli bölüm) — tembel yüklenir                        */
/* ------------------------------------------------------------------ */
const mapDialog = $('#soz-haritasi')
let mapController: { invalidate(): void } | null = null
let mapLoading: Promise<void> | null = null
let mapReturnFocus: HTMLElement | null = null

async function openWordMap(fromHash = false) {
  if (!mapDialog) return
  mapReturnFocus = document.activeElement as HTMLElement | null
  mapDialog.hidden = false
  document.body.classList.add('has-wordmap')
  if (!fromHash) history.replaceState(null, '', '#soz-haritasi')
  $<HTMLButtonElement>('#wordmap-close')?.focus({ preventScroll: true })
  if (!mapLoading) {
    mapLoading = import('./wordmap')
      .then((m) => m.initWordMap())
      .then((c) => {
        mapController = c
      })
      .catch((err) => {
        console.error('Söz Haritası yüklenemedi:', err)
        const loading = $('#wordmap-loading')
        if (loading) loading.textContent = 'Harita yüklenemedi. Sayfayı yenileyip tekrar dene.'
      })
  }
  await mapLoading
  requestAnimationFrame(() => mapController?.invalidate())
}

function closeWordMap() {
  if (!mapDialog || mapDialog.hidden) return
  if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined)
  mapDialog.hidden = true
  document.body.classList.remove('has-wordmap')
  if (location.hash === '#soz-haritasi') history.replaceState(null, '', location.pathname)
  ;(mapReturnFocus ?? $('#map-seal'))?.focus({ preventScroll: true })
}

$('#map-seal')?.addEventListener('click', (e) => {
  e.preventDefault()
  void openWordMap()
})
$('#wordmap-close')?.addEventListener('click', closeWordMap)
document.addEventListener('keydown', (e) => {
  if (!mapDialog || mapDialog.hidden) return
  if (e.key === 'Escape') {
    if (document.fullscreenElement) return // önce tarayıcı tam ekrandan çıkar
    const result = $('#wordmap-result')
    if (result && !result.hidden) {
      e.preventDefault()
      result.hidden = true
      return
    }
    e.preventDefault()
    closeWordMap()
    return
  }
  if (e.key === 'Tab') {
    // Odak hapsi: diyalog dışına çıkılmasın
    const focusables = $$<HTMLElement>(
      'button:not([disabled]), a[href], summary, [tabindex]:not([tabindex="-1"])',
      mapDialog
    ).filter((n) => n.offsetParent !== null)
    if (!focusables.length) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }
})
window.addEventListener('hashchange', () => {
  if (location.hash === '#soz-haritasi') void openWordMap(true)
  else closeWordMap()
})
window.addEventListener('resize', () => mapController?.invalidate())

handleInitialHash()
if (location.hash === '#soz-haritasi') void openWordMap(true)
document.documentElement.classList.add('is-ready')

// Geliştirme/test kolaylığı: veri bütünlüğünü konsola yazmadan doğrula
if (WORDS.length !== 12) console.error('Kelime listesi 12 öğe içermiyor!')
